import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../api/httpClient";
import assetsImg from "../../assets/assetsImg.png";
import useCurrentEmployee from "../../hooks/useCurrentEmployee";
import { getApiBaseUrl } from "../../data/authStorage";
import { Modal } from "../../components/modal";
import PerformanceStatusBadge, { getPerformanceStatusMeta } from "../../components/performance/PerformanceStatusBadge";
import ExpandableSearch from "../../components/ExpandableSearch";
import ViewActionButton from "../../components/table/ViewActionButton";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";

const frequencies = ["Monthly", "Quarterly", "Twice a year", "Yearly"];

const getMessage = (error, fallback) => {
  const responseMessage = error?.response?.data?.message || fallback;
  return Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage;
};

export default function PerformanceEvaluation() {
  const { companyAccount, currentEmployee, loading: employeeLoading } = useCurrentEmployee();
  const [evaluationFilter, setEvaluationFilter] = useState("all");
  const [evaluations, setEvaluations] = useState([]);
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusIndicatorFilter, setStatusIndicatorFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [updatingId, setUpdatingId] = useState("");
  const [settingsItem, setSettingsItem] = useState(null);
  const [settingsForm, setSettingsForm] = useState({
    frequency: "Yearly",
    status: "Active",
    selfAssessmentOpen: false,
    managerAssessmentOpen: false,
  });
  const [settingsError, setSettingsError] = useState("");
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const apiBaseUrl = getApiBaseUrl();
  const navigate = useNavigate();
  const isAdmin = Boolean(companyAccount || currentEmployee?.modulePermissions?.includes("performance-evaluation"));

  const loadEvaluations = useCallback(async () => {
    if (!apiBaseUrl) {
      setFetchError("API base URL is not configured.");
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setFetchError("");
      const response = await axios.get(`${apiBaseUrl}/organizations/performance-evaluations`);
      setEvaluations(response?.data?.evaluations || []);
    } catch (error) {
      setFetchError(getMessage(error, "Unable to load performance evaluations."));
      setEvaluations([]);
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    void loadEvaluations();
  }, [loadEvaluations]);

  useEffect(() => {
    if (!employeeLoading && !isAdmin && evaluationFilter !== "my") {
      setEvaluationFilter("my");
    }
  }, [employeeLoading, evaluationFilter, isAdmin]);

  const roleOptions = useMemo(() => {
    const roleMap = new Map();
    evaluations.forEach((item) => {
      if (item.role?.id) {
        roleMap.set(item.role.id, item.role.name || "Unnamed role");
      }
    });
    return Array.from(roleMap, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [evaluations]);

  const scopedData = useMemo(() => {
    const visible = !isAdmin || evaluationFilter === "my"
      ? evaluations.filter((item) => !currentEmployee?.id || item.employee?.id === currentEmployee.id)
      : evaluations;

    if (isAdmin && roleFilter !== "all") {
      return visible.filter((item) => item.role?.id === roleFilter);
    }

    return visible;
  }, [currentEmployee?.id, evaluationFilter, evaluations, isAdmin, roleFilter]);

  const currentData = useMemo(
    () => statusIndicatorFilter === "all"
      ? scopedData
      : scopedData.filter((item) => item.performanceStatusCode === statusIndicatorFilter),
    [scopedData, statusIndicatorFilter],
  );

  const visibleData = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return currentData;
    return currentData.filter((item) =>
      [item.employee?.fullName, item.employee?.officialEmail, item.employee?.personalEmail, item.department?.name, item.role?.name, item.frequency, item.performanceStatus]
        .some((value) => String(value || "").toLowerCase().includes(query)),
    );
  }, [currentData, searchTerm]);

  const performanceStatusCounts = useMemo(() => scopedData.reduce(
    (counts, item) => ({ ...counts, [item.performanceStatusCode || "BEHIND"]: (counts[item.performanceStatusCode || "BEHIND"] || 0) + 1 }),
    { ON_TRACK: 0, AT_RISK: 0, BEHIND: 0 },
  ), [scopedData]);

  const openSettings = (item) => {
    setSettingsItem(item);
    setSettingsForm({
      frequency: item.frequency || "Yearly",
      status: item.templateStatus || "Active",
      selfAssessmentOpen: Boolean(item.selfAssessmentOpen),
      managerAssessmentOpen: Boolean(item.managerAssessmentOpen),
    });
    setSettingsError("");
  };

  const closeSettings = () => {
    if (settingsSaving) return;
    setSettingsItem(null);
    setSettingsError("");
  };

  const saveSettings = async () => {
    const item = settingsItem;
    const evaluationId = item.evaluationId || item.evaluation?.id;
    if (!evaluationId) return;

    try {
      setSettingsSaving(true);
      setSettingsError("");
      setUpdatingId(`${item.id}-settings`);
      await axios.patch(`${apiBaseUrl}/organizations/performance-evaluations/${evaluationId}/settings`, settingsForm);
      await loadEvaluations();
      setSettingsItem(null);
    } catch (error) {
      setSettingsError(getMessage(error, "Unable to update KPI settings."));
    } finally {
      setSettingsSaving(false);
      setUpdatingId("");
    }
  };

  return (
    <div className="w-full p-4 sm:p-6 font-Nunito">
      <div className="mb-5 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-black">Performance Evaluation</h1>
          <p className="mt-1 text-sm text-slate-500">Review, score and manage staff KPI assignments.</p>
        </div>
        {isAdmin ? (
          <button
            onClick={() => navigate("/performance-evaluation/new-evaluation")}
            className="w-full rounded-lg bg-[#1234CD] px-5 py-3 text-sm font-semibold text-white sm:w-auto"
          >
            Add new KPI
          </button>
        ) : null}
      </div>

      <div className="bg-white p-3 sm:p-6">
        <div className="mb-5 grid grid-cols-2 items-center gap-2 sm:flex sm:flex-wrap">
          {isAdmin ? (
            <>
              <button
                onClick={() => setEvaluationFilter("all")}
                className={`rounded-lg px-3 py-2 text-sm font-semibold sm:px-4 ${evaluationFilter === "all" ? "bg-blue-50 text-blue-700" : "text-slate-600"}`}
              >
                All staff KPI
              </button>
              <button
                onClick={() => setEvaluationFilter("my")}
                className={`rounded-lg px-3 py-2 text-sm font-semibold sm:px-4 ${evaluationFilter === "my" ? "bg-blue-50 text-blue-700" : "text-slate-600"}`}
              >
                My KPI
              </button>
              <select
                value={roleFilter}
                onChange={(event) => setRoleFilter(event.target.value)}
                className="col-span-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 outline-none focus:border-blue-500 sm:ml-auto sm:w-auto sm:py-2"
              >
                <option value="all">All roles</option>
                {roleOptions.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </>
          ) : (
            <span className="rounded-lg bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700">My department KPI</span>
          )}
        </div>

        <div className="mb-5 grid gap-3 sm:grid-cols-3">
          {[
            ["ON_TRACK", "On Track", "Healthy score and evaluation progress"],
            ["AT_RISK", "At Risk", "Needs follow-up to remain on schedule"],
            ["BEHIND", "Behind", "Significant progress or score gap"],
          ].map(([code, label, description]) => {
            const meta = getPerformanceStatusMeta(code, label);
            const active = statusIndicatorFilter === code;
            return (
              <button
                key={code}
                type="button"
                onClick={() => setStatusIndicatorFilter(active ? "all" : code)}
                className={`rounded-xl border p-4 text-left transition ${active ? meta.className : "border-slate-100 bg-white hover:border-slate-200"}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <PerformanceStatusBadge code={code} compact />
                  <span className="text-2xl font-bold text-slate-900">{performanceStatusCounts[code] || 0}</span>
                </div>
                <p className="mt-3 text-xs text-slate-500">{description}</p>
              </button>
            );
          })}
        </div>

        <div className="mb-5 rounded-xl border border-blue-100 bg-blue-50/60 px-4 py-3 text-xs leading-5 text-blue-800">
          Status combines completion and aggregate score: below 40% completion is <strong>Behind</strong>, 40–74% is <strong>At Risk</strong>; at 75%+ completion, scores below 50% are Behind, 50–69% are At Risk, and 70%+ are On Track.
        </div>

        <ExpandableSearch
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search evaluations"
          label="Search performance evaluations"
          className="mb-4"
        />

        {fetchError ? (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
            {fetchError}
          </div>
        ) : null}

        {isLoading ? <FetchLoadingState message="Loading evaluations..." className="min-h-[180px]" /> : null}

        <div>
          <div className="hidden min-w-[1080px] grid-cols-[2fr,1.2fr,1.1fr,0.8fr,0.8fr,1fr,1.8fr,0.8fr] gap-4 border-b border-slate-100 px-4 py-3 text-sm font-bold text-black lg:grid">
            <div>Staff</div>
            <div>Department</div>
            <div>Role</div>
            <div>Frequency</div>
            <div>Total (%)</div>
            <div>Progress</div>
            <div>Windows</div>
            <div>Actions</div>
          </div>

          <div className="min-w-0 lg:min-w-[1080px]">
            {visibleData.map((item) => (
              <div
                key={item.id}
                className="mb-3 grid grid-cols-2 gap-3 rounded-xl border border-slate-100 p-4 text-sm text-slate-800 shadow-sm lg:mb-0 lg:grid-cols-[2fr,1.2fr,1.1fr,0.8fr,0.8fr,1fr,1.8fr,0.8fr] lg:items-center lg:gap-4 lg:rounded-none lg:border-x-0 lg:border-t-0 lg:px-4 lg:py-4 lg:shadow-none"
              >
                <div className="col-span-2 flex min-w-0 items-center gap-3 lg:col-span-1">
                  <div className="h-10 w-10 overflow-hidden rounded-full bg-slate-100">
                    <img
                      src={item.employee?.profilePhotoUrl || assetsImg}
                      alt={item.employee?.fullName || "Staff"}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-black">{item.employee?.fullName || "Unknown staff"}</p>
                    <p className="truncate text-xs text-slate-500">{item.employee?.officialEmail || item.employee?.personalEmail || ""}</p>
                  </div>
                </div>
                <div><span className="mb-1 block text-[10px] font-semibold uppercase text-slate-400 lg:hidden">Department</span>{item.department?.name || "-"}</div>
                <div><span className="mb-1 block text-[10px] font-semibold uppercase text-slate-400 lg:hidden">Role</span>{item.role?.name || "-"}</div>
                <div><span className="mb-1 block text-[10px] font-semibold uppercase text-slate-400 lg:hidden">Frequency</span>{item.frequency || "-"}</div>
                <div className="font-semibold text-black"><span className="mb-1 block text-[10px] font-semibold uppercase text-slate-400 lg:hidden">Total</span>{Math.min(100, Number(item.totalScore) || 0)}%</div>
                <div className="col-span-2 lg:col-span-1">
                  <div className="mb-1 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                    <div
                      className={`h-full rounded-full ${item.performanceStatusCode === "ON_TRACK" ? "bg-emerald-500" : item.performanceStatusCode === "AT_RISK" ? "bg-amber-500" : "bg-rose-500"}`}
                      style={{ width: `${Math.min(100, Number(item.completionPercent) || 0)}%` }}
                    />
                  </div>
                  <p className="text-xs font-semibold text-slate-600">{item.progressLabel || `${item.completionPercent || 0}% completed`}</p>
                  <div className="mt-2">
                    <PerformanceStatusBadge code={item.performanceStatusCode} label={item.performanceStatus} reason={item.performanceStatusReason} compact />
                  </div>
                  <p className="mt-1 line-clamp-2 text-[11px] leading-4 text-slate-400">{item.performanceStatusReason}</p>
                </div>
                <div className="col-span-2 flex flex-wrap gap-2 lg:col-span-1">
                  {isAdmin ? (
                    <>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${item.selfAssessmentOpen ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                        Staff {item.selfAssessmentOpen ? "open" : "closed"}
                      </span>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${item.managerAssessmentOpen ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                        Managers {item.managerAssessmentOpen ? "open" : "closed"}
                      </span>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${item.templateStatus === "Active" ? "bg-blue-50 text-blue-700" : "bg-red-50 text-red-700"}`}>
                        {item.templateStatus || "Active"}
                      </span>
                      <button
                        type="button"
                        onClick={() => openSettings(item)}
                        disabled={updatingId === `${item.id}-settings`}
                        className="rounded-full border border-blue-200 px-3 py-1 text-xs font-semibold text-blue-700"
                      >
                        Settings
                      </button>
                    </>
                  ) : (
                    <>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${item.selfAssessmentOpen ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                        Staff {item.selfAssessmentOpen ? "open" : "closed"}
                      </span>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${item.managerAssessmentOpen ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                        Managers {item.managerAssessmentOpen ? "open" : "closed"}
                      </span>
                    </>
                  )}
                </div>
                <div className="col-span-2 flex justify-end lg:col-span-1 lg:justify-start">
                  <ViewActionButton to={`/performance-evaluation/${item.id}`} label={`View evaluation for ${item.employee?.fullName || "staff"}`} />
                </div>
              </div>
            ))}

            {!isLoading && visibleData.length === 0 ? (
              <div className="px-4 py-8 text-sm text-slate-500">{searchTerm ? "No matching KPI evaluations found." : "No KPI evaluations found."}</div>
            ) : null}
          </div>
        </div>

        <div className="mt-4 text-sm text-slate-500">Showing {visibleData.length} KPI evaluations</div>
      </div>

      <Modal isOpen={Boolean(settingsItem)} onClose={closeSettings}>
        <div className="font-Nunito">
          <h2 className="mb-1 text-xl font-semibold text-black">KPI settings</h2>
          <p className="mb-5 text-sm text-slate-500">
            Update frequency, status, and when staff or managers can respond.
          </p>

          {settingsError ? (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
              {settingsError}
            </div>
          ) : null}

          <div className="space-y-4">
            <label className="block">
              <span className="mb-1 block text-sm font-semibold text-slate-700">Frequency</span>
              <select
                value={settingsForm.frequency}
                onChange={(event) => setSettingsForm((prev) => ({ ...prev, frequency: event.target.value }))}
                className="w-full rounded-lg border border-slate-300 px-3 py-3 text-sm outline-none focus:border-blue-500"
              >
                {frequencies.map((frequency) => (
                  <option key={frequency} value={frequency}>
                    {frequency}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-1 block text-sm font-semibold text-slate-700">Status</span>
              <select
                value={settingsForm.status}
                onChange={(event) => setSettingsForm((prev) => ({ ...prev, status: event.target.value }))}
                className="w-full rounded-lg border border-slate-300 px-3 py-3 text-sm outline-none focus:border-blue-500"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </label>

            <div className="rounded-lg border border-slate-200 p-3">
              <label className="flex items-start gap-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={settingsForm.selfAssessmentOpen}
                  onChange={(event) => setSettingsForm((prev) => ({ ...prev, selfAssessmentOpen: event.target.checked }))}
                  className="mt-1 h-4 w-4"
                />
                <span>
                  <span className="block font-semibold text-slate-900">Open staff self scoring</span>
                  Staff can enter their self score when this is enabled.
                </span>
              </label>
            </div>

            <div className="rounded-lg border border-slate-200 p-3">
              <label className="flex items-start gap-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={settingsForm.managerAssessmentOpen}
                  onChange={(event) => setSettingsForm((prev) => ({ ...prev, managerAssessmentOpen: event.target.checked }))}
                  className="mt-1 h-4 w-4"
                />
                <span>
                  <span className="block font-semibold text-slate-900">Open manager scoring</span>
                  HODs and organization admins can score their columns when this is enabled.
                </span>
              </label>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={closeSettings}
              disabled={settingsSaving}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveSettings}
              disabled={settingsSaving}
              className="rounded-lg bg-[#1234CD] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {settingsSaving ? "Saving..." : "Save settings"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
